import { Card, CardActions, CardContent, Divider, Stack } from '@mui/material';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import {
  FormikMultiAutocompleteField,
  FormikTextField,
} from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import type { RoleName } from '@constants/role_name';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useSession } from '@hooks/useSession';

import { useGetAccountRecord } from 'generated/account.generated';
import { useAdminListRoles } from 'generated/admin.generated';
import { usePostUserRecord } from 'generated/user.generated';

const messages = defineMessages({
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  firstName: {
    id: 'GLOBAL.firstName',
    defaultMessage: 'First Name',
  },
  middleName: {
    id: 'GLOBAL.middleName',
    defaultMessage: 'Middle Name',
  },
  lastName: {
    id: 'GLOBAL.lastName',
    defaultMessage: 'Last Name',
  },
  roles: {
    id: 'admin.users.roles',
    defaultMessage: 'Roles',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  submit: {
    id: 'admin.users.create.submit',
    defaultMessage: 'Submit',
  },
});

type FormValues = {
  email: string;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  roles: { label: RoleName; value: string }[];
};

interface NewInternalUserFormProps {
  /** Account ID the new user should be in */
  account_id?: string;
  /** Should the Cognito invitation be sent? */
  send_invite?: boolean;
}

const NewInternalUserForm: React.FC<NewInternalUserFormProps> = ({
  account_id,
  send_invite = true,
}) => {
  const { formatMessage } = useIntl();
  const { emailSchema } = useCommonSchemas();
  const {
    state: { activeProfile },
  } = useSession();

  const [createUser, { loading: createUserLoading }] = usePostUserRecord();
  const { data: rolesData, loading: rolesLoading } = useAdminListRoles();

  const accountId = account_id ?? activeProfile?.account_id ?? '';
  const { data: accountData, loading: accountLoading } = useGetAccountRecord({
    variables: {
      input: { account_id: accountId },
    },
    skip: !accountId,
  });
  const dataLoading = rolesLoading || accountLoading;
  const registerLoading = createUserLoading;

  const validationSchema: Yup.Schema<FormValues> = Yup.object().shape({
    email: emailSchema.required(formatMessage(messages.required)),
    first_name: Yup.string().required(formatMessage(messages.required)),
    middle_name: Yup.string().nullable(),
    last_name: Yup.string().required(formatMessage(messages.required)),
    roles: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string<RoleName>().required(),
          value: Yup.string().required(),
        })
      )
      .min(1, formatMessage(messages.required))
      .required(formatMessage(messages.required)),
  });

  const initialValues: FormValues = {
    email: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    roles: [],
  };

  async function handleSubmit(
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>
  ) {
    if (!account_id) {
      return;
    }
    const { roles, ...input } = values;

    await createUser({
      variables: {
        account_id,
        input: {
          ...input,
          roles: roles.map(({ label }) => label),
          send_invite,
        },
      },
    });
    resetForm();
  }

  const accountType = accountData?.getAccountRecord.account_type;
  const roleOptions = (rolesData?.listRoles ?? [])
    .filter(({ scope }) => !accountType || scope === accountType)
    .map((role) => ({
      label: role.role_name,
      value: role.role_id,
    }));

  return (
    <Card>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <CardContent>
              <FormikMultiAutocompleteField
                name="roles"
                options={roleOptions}
                label={formatMessage(messages.roles)}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.label
                }
                required
              />
              <Stack direction="row" spacing={2}>
                <FormikTextField
                  label={formatMessage(messages.firstName)}
                  name="first_name"
                  fullWidth
                  required
                />
                <FormikTextField
                  label={formatMessage(messages.middleName)}
                  name="middle_name"
                  fullWidth
                />
                <FormikTextField
                  label={formatMessage(messages.lastName)}
                  name="last_name"
                  fullWidth
                  required
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormikTextField
                  label={formatMessage(messages.email)}
                  name="email"
                  type="email"
                  fullWidth
                  required
                />
              </Stack>
            </CardContent>
            <Divider />
            <CardActions>
              <RoundedButton
                disabled={isSubmitting || registerLoading || dataLoading}
                type="submit"
              >
                {formatMessage(messages.submit)}
              </RoundedButton>
            </CardActions>
          </Form>
        )}
      </Formik>
    </Card>
  );
};

export default NewInternalUserForm;
